import * as React from "react";
import styled from "styled-components";
import { Link } from "gatsby";

import { Container } from "@ui/Atoms/Grid";
import theme from "@ui/themes/default";
import { Heading, Button, Paragraph, Segment } from "@ui/Atoms";
import { Check } from "@ui/Assets/Symbolicons/Check";

import { getUrlData } from "@services/GetUrlData";

interface AdvantagesListItem {
  advantages_list_item: { text: string };
}

export interface AdvantagesData {
  advantages_title?: { text: string };
  advantages_list?: AdvantagesListItem[];
  advantages_action_button?: { text: string };
  advantages_action_button_link?: { text: string };
}

interface Props {
  data: AdvantagesData;
}

const Advantages = ({ data }: Props): React.ReactElement => {
  const { isWWWApp } = getUrlData(
    data.advantages_action_button_link?.text || "",
  );

  return (
    <Background>
      <StyledContainer>
        <StyledSegment>
          <Title type="h2" styleAs="h4">
            {data.advantages_title?.text || ""}
          </Title>
          <List>
            {/* PRISMIC */}
            {data.advantages_list?.map(advantage => (
              <ListItem key={advantage.advantages_list_item.text}>
                <CheckIcon>
                  <Check size={16} color={theme.palette.green500} />
                </CheckIcon>
                <Paragraph type="body">
                  {advantage.advantages_list_item.text}
                </Paragraph>
              </ListItem>
            ))}
          </List>

          {isWWWApp && (
            <Link to={data.advantages_action_button_link?.text || ""}>
              <Button size="fill">
                {data.advantages_action_button?.text || ""}
              </Button>
            </Link>
          )}

          {!isWWWApp && (
            <a href={data.advantages_action_button_link?.text || ""}>
              <Button size="fill">
                {data.advantages_action_button?.text || ""}
              </Button>
            </a>
          )}
        </StyledSegment>
      </StyledContainer>
    </Background>
  );
};

export { Advantages };

const StyledContainer = styled(Container)`
  display: flex;
  justify-content: center;
`;

const StyledSegment = styled(Segment)`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  a {
    width: 100%;
  }
`;

const List = styled.ul`
  margin: 0;
  list-style-type: none;
  margin-bottom: ${theme.margins.base_x4};

  ${props => props.theme.breakpoints.desktop} {
    padding: 0;
  }
`;

const ListItem = styled.li`
  position: relative;
  margin-bottom: ${props => props.theme.margins.base_x2};
  text-align: left;
  line-height: ${props => props.theme.fonts.lineHeights.subtitle};

  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled.div`
  position: absolute;
  right: 100%;
  margin-right: ${theme.margins.base_x2};
`;

const Background = styled.div`
  background: ${theme.palette.white};
`;

const Title = styled(Heading)`
  margin-bottom: ${theme.margins.base_x6};
  font-weight: ${theme.fonts.weights.bold};
`;
